import React from "react";
import { translateService, stateService, signService } from "../../services/imports";
import signers from "../../services/signerManagementService";
import OptimizedComponent from "../../components/optimizedComponent";
import { LoadingButton } from "../../components/formcontrols/imports";
import { states, stateEvents } from "../../models/states";
import { AppContext } from "../../Context/AppContext";
import SignerCard from "./signerCard";
import AddSignerDialog from "./addSignerDialog";
import CredentialsDialog from "./credentialsDialog";
import { RequestCertificateDialog, UploadCertificateDialog } from "./certificateDialogs";

// a cloud signing tool's certificate awaiting remote-signer: poll it every
// 5 s, for up to 6 minutes (an unanswered request turns FAILED after 5)
const POLL_MS = 5000;
const POLL_FOR_MS = 6 * 60 * 1000;

const t = (k, ...a) => translateService.t(`signers.${k}`, ...a);

const awaitingRemoteSigner = (s) => {
  const c = s.certificate;
  if (!c) return false;
  if (c.status === "REQUESTED" && !c.csrAvailable && !c.answeredAt) return true;
  return !!(c.pendingRequest && !c.pendingRequest.answeredAt);
};

// The workspace's signing tools page (the gateway's website prompts A1, B1):
// every signing tool of the workspace selected in the header, active and
// deactivated, oldest first, as cards (signer management API, GET
// .../workspace/{ws}/signer). The owner adds, renames, deactivates and
// reactivates them, enters their verification credentials, and runs a cloud
// signing tool's certificate (request, CSR download and certificate upload,
// download, delete one never used); a member reads the list, and may add the
// kinds the gateway makes by itself (Georgian eID). Labels come from the API.
export default class SignaturePage extends OptimizedComponent {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      workspaceId: null,
      list: null, // { owner, creatableKinds, signers }
      catalogue: null, // the labels of the cards
      loading: true,
      loadError: null,
      signInRequired: false,
      busy: {}, // uuid -> the running action
      messages: {}, // uuid -> { text, error }
      highlight: null, // the uuid just added
      adding: false,
      credentialsFor: null, // { signer, note }
      requestFor: null, // a signing tool without any certificate request
      uploadFor: null,
    };
    this.pollUntil = {}; // uuid -> when to stop polling
  }

  componentDidMount() {
    stateService.event(stateEvents.scrollUp);
    // the API's labels are in the app's language: read them again on a switch
    this.secureSubscription(
      translateService.subscribe(() =>
        this.setState({ lang: translateService.key }, () => {
          this.loadCatalogue();
          this.load();
        })
      )
    );
    // emits at once and again whenever the header switches the workspace
    this.secureSubscription(
      stateService.getState(states.userInfo).subscribe((info) => {
        const workspaceId = info && info.workspaceExposedId;
        if (!workspaceId) {
          if (!this.state.workspaceId) this.setState({ loading: false });
          return;
        }
        if (workspaceId === this.state.workspaceId) return;
        this.pollUntil = {};
        this.setState({ workspaceId, list: null, busy: {}, messages: {}, highlight: null }, this.load);
      })
    );
    this.loadCatalogue();
    const timer = setInterval(this.poll, POLL_MS);
    this.secureSubscription({ unsubscribe: () => clearInterval(timer) });
  }

  // ---- loading -----------------------------------------------------------------------

  load = () => {
    const workspaceId = this.state.workspaceId;
    if (!workspaceId) return;
    this.setState({ loading: true, loadError: null });
    this.secureSubscription(
      signers.list(workspaceId).subscribe(
        (res) => {
          if (workspaceId !== this.state.workspaceId) return;
          const list = res.data || { signers: [] };
          this.setState({ list, loading: false, signInRequired: false });
          (list.signers || []).forEach(this.watch);
        },
        (e) => {
          if (workspaceId !== this.state.workspaceId) return;
          const signIn = signService.problemCode(e) === "MANAGEMENT_SIGN_IN_NOT_ACCEPTED";
          this.setState({ loading: false, signInRequired: signIn, loadError: signIn ? null : this.problem(e, "loadError") });
        }
      )
    );
  };

  // the catalogue's labels (authentication types, engines) in the app's language
  loadCatalogue = () =>
    this.secureSubscription(signers.catalogue().subscribe((res) => this.setState({ catalogue: res.data || null }), () => {}));

  // start polling a certificate awaiting remote-signer
  watch = (s) => {
    if (awaitingRemoteSigner(s) && !this.pollUntil[s.uuid]) this.pollUntil[s.uuid] = Date.now() + POLL_FOR_MS;
  };

  poll = () => {
    const workspaceId = this.state.workspaceId;
    if (!workspaceId || document.hidden || !this.props.display) return;
    Object.keys(this.pollUntil).forEach((uuid) => {
      if (Date.now() > this.pollUntil[uuid]) return delete this.pollUntil[uuid];
      if (this.polling && this.polling[uuid]) return;
      this.polling = { ...(this.polling || {}), [uuid]: true };
      signers.get(workspaceId, uuid).subscribe(
        (res) => {
          this.polling[uuid] = false;
          if (workspaceId !== this.state.workspaceId || !res.data) return;
          this.replace(res.data);
          if (!awaitingRemoteSigner(res.data)) delete this.pollUntil[uuid];
        },
        () => (this.polling[uuid] = false)
      );
    });
  };

  // ---- the cards' actions ---------------------------------------------------------------

  // the text for a refusal: the gateway's own detail where it has one
  problem = (e, fallbackKey) => {
    const code = signService.problemCode(e);
    if (code === "MANAGEMENT_SIGN_IN_NOT_ACCEPTED") return t("signInRequired");
    if (code === "GENERAL_PROCESSING_ERROR") return t("generalError");
    if (code === "VERIFICATION_ENGINE_UNAVAILABLE") return t("engineUnreachable");
    if (code === "SIGNER_QUEUE_UNAVAILABLE") return t("queueUnavailable");
    return signService.problemMessage(e, t(fallbackKey || "actionError"));
  };

  replace = (signer) =>
    this.setState((s) => {
      if (!s.list) return null;
      const known = s.list.signers.some((x) => x.uuid === signer.uuid);
      return {
        list: {
          ...s.list,
          signers: known ? s.list.signers.map((x) => (x.uuid === signer.uuid ? signer : x)) : [...s.list.signers, signer],
        },
      };
    });

  setBusy = (uuid, action) => this.setState((s) => ({ busy: { ...s.busy, [uuid]: action } }));

  message = (uuid, text, error = false) =>
    this.setState((s) => ({ busy: { ...s.busy, [uuid]: null }, messages: { ...s.messages, [uuid]: text ? { text, error } : null } }));

  // one action on one card: busy while it runs, the answer replaces the card;
  // resolves true when it went through
  run = (signer, action, observable, done) =>
    new Promise((resolve) => {
      this.setState((s) => ({
        busy: { ...s.busy, [signer.uuid]: action },
        messages: { ...s.messages, [signer.uuid]: null },
      }));
      observable.subscribe(
        (res) => {
          this.setBusy(signer.uuid, null);
          if (res && res.data && res.data.uuid) {
            this.replace(res.data);
            this.watch(res.data);
          }
          if (done) done(res);
          resolve(true);
        },
        (e) => {
          // gone, or another workspace's: the list again
          if (signService.problemCode(e) === "SIGNER_NOT_FOUND") this.load();
          this.message(signer.uuid, this.problem(e), true);
          resolve(false);
        }
      );
    });

  actionsFor = (s) => {
    const ws = this.state.workspaceId;
    return {
      rename: (name) => this.run(s, "rename", signers.rename(ws, s.uuid, name)),
      evidence: (verificationEvidence) => this.run(s, "evidence", signers.setEvidence(ws, s.uuid, verificationEvidence)),
      deactivate: () => this.run(s, "deactivate", signers.deactivate(ws, s.uuid)),
      activate: () => this.run(s, "activate", signers.activate(ws, s.uuid)),
      validate: () =>
        this.run(s, "validate", signers.validateCredentials(ws, s.uuid), () => this.message(s.uuid, t("credentials.checked"))),
      credentials: () => this.setState({ credentialsFor: { signer: s, note: false } }),
      request: () => this.setState({ requestFor: s }),
      requestAgain: () => this.run(s, "requestAgain", signers.requestCertificate(ws, s.uuid)),
      downloadRequest: () => this.run(s, "downloadRequest", signers.downloadRequest(ws, s.uuid)),
      downloadCertificate: () => this.run(s, "downloadCertificate", signers.downloadCertificate(ws, s.uuid)),
      upload: () => this.setState({ uploadFor: s }),
      remove: () => {
        this.setState((st) => ({ busy: { ...st.busy, [s.uuid]: "delete" }, messages: { ...st.messages, [s.uuid]: null } }));
        signers.remove(ws, s.uuid).subscribe(
          () =>
            this.setState((st) => ({
              busy: { ...st.busy, [s.uuid]: null },
              list: st.list && { ...st.list, signers: st.list.signers.filter((x) => x.uuid !== s.uuid) },
            })),
          (e) =>
            this.message(
              s.uuid,
              signService.problemCode(e) === "SIGNER_IN_USE" ? t("deleteInUse") : this.problem(e),
              true
            )
        );
      },
    };
  };

  // ---- the dialogs ---------------------------------------------------------------------

  // a new signing tool (or the workspace's own Georgian eID one): its card,
  // and the credentials form straight away when it waits for them
  onCreated = (signer) => {
    this.setState({ adding: false, highlight: signer.uuid });
    this.replace(signer);
    this.watch(signer);
    const missing = (signer.provisioning && signer.provisioning.missing) || [];
    if (signer.provisioning && signer.provisioning.state === "PENDING" && missing.includes("ENGINE_CREDENTIALS"))
      this.setState({ credentialsFor: { signer, note: true } });
    // the kinds one may still add (a one-per-workspace kind is taken now)
    this.load();
  };

  saved = (key) => (signer) => {
    this.setState({ [key]: null });
    this.replace(signer);
    this.watch(signer);
  };

  kindEntry = (signer) =>
    ((this.state.catalogue && this.state.catalogue.kinds) || []).find((k) => k.kind === (signer && signer.kind)) || null;

  // ---- rendering -------------------------------------------------------------------------

  renderList() {
    const { list, loading, loadError, signInRequired, catalogue, busy, messages, highlight } = this.state;
    if (signInRequired) return <div className="message">{t("signInRequired")}</div>;
    if (loading && !list) return <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />;
    if (loadError)
      return (
        <div className="message load-error">
          <span>{loadError}</span>
          <div className="buttons">
            <LoadingButton type="button" className="button background-color-two color-white" onClick={this.load}>
              {translateService.t("signature.retry")}
            </LoadingButton>
          </div>
        </div>
      );
    if (!list) return null;
    const items = list.signers || [];
    return (
      <React.Fragment>
        {!list.owner && <p className="signers-page__note">{t("readOnly")}</p>}
        {!items.length && <div className="message">{translateService.t("signature.empty")}</div>}
        <div className="signers-page__cards">
          {items.map((s) => (
            <SignerCard
              key={s.uuid}
              signer={s}
              catalogue={catalogue}
              owner={!!list.owner}
              busy={busy[s.uuid] || null}
              message={messages[s.uuid] || null}
              highlight={highlight === s.uuid}
              polling={!!this.pollUntil[s.uuid]}
              actions={this.actionsFor(s)}
            />
          ))}
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { list, workspaceId, credentialsFor, requestFor, uploadFor, catalogue } = this.state;
    const creatable = (list && list.creatableKinds) || [];
    // what an organization's account offers, shown disabled (a personal
    // workspace's cloud kinds): the dialog opens for them too
    const unavailable = (list && list.unavailableKinds) || [];
    return (
      <div className={`package-info signature-page signers-page container ${translateService.key} ${!this.props.display ? "d-none" : ""}`}>
        <div className="page-header">
          <span className="clickable" onClick={() => stateService.event(stateEvents.openModalPage, null)}>
            <img src="../../assets/images/close32.svg" alt="" />
          </span>
        </div>
        <div className="package-info-items">
          <div className="flex-title-item">
            <div className="title-container">
              <span className="title">{translateService.t("signature.title")}</span>
            </div>
            <div className="accinfoF">
              <span className="account-info">
                <div className="ai">
                  <span className="account-number">{this.context.state.activeDomain}</span>
                  <span className="account-domain-name">{translateService.t("common.accountDomainName")}</span>
                </div>
              </span>
            </div>
          </div>
          {(creatable.length > 0 || unavailable.length > 0) && (
            <div className="signers-page__toolbar">
              <LoadingButton
                type="button"
                className="button background-color-two color-white"
                onClick={() => this.setState({ adding: true })}
              >
                {t("add.title")}
              </LoadingButton>
            </div>
          )}
          <div className="signers-list">{this.renderList()}</div>
        </div>

        <AddSignerDialog
          isOpen={this.state.adding}
          workspaceId={workspaceId}
          creatableKinds={creatable}
          unavailableKinds={unavailable}
          onClose={() => this.setState({ adding: false })}
          onCreated={this.onCreated}
        />
        <CredentialsDialog
          isOpen={!!credentialsFor}
          workspaceId={workspaceId}
          signer={credentialsFor && credentialsFor.signer}
          note={!!(credentialsFor && credentialsFor.note)}
          onClose={() => this.setState({ credentialsFor: null })}
          onSaved={this.saved("credentialsFor")}
        />
        <RequestCertificateDialog
          isOpen={!!requestFor}
          workspaceId={workspaceId}
          signer={requestFor}
          kind={this.kindEntry(requestFor)}
          catalogue={catalogue}
          onClose={() => this.setState({ requestFor: null })}
          onSaved={this.saved("requestFor")}
        />
        <UploadCertificateDialog
          isOpen={!!uploadFor}
          workspaceId={workspaceId}
          signer={uploadFor}
          onClose={() => this.setState({ uploadFor: null })}
          onSaved={this.saved("uploadFor")}
          onReload={(text) => {
            const uuid = uploadFor && uploadFor.uuid;
            this.setState({ uploadFor: null });
            this.load();
            if (uuid) this.message(uuid, text, true);
          }}
        />
      </div>
    );
  }
}
